import { useAuth0 } from "@auth0/auth0-react";
import { useFormikContext } from "formik";
import { useState } from "react";
import { Button } from "react-bootstrap";
import { Loader } from "../common/progress/Loader";
import { ExtendedUser } from "../identity/models/ExtendedUser";
import { useVersionActions } from "./hooks/useVersionActions";
import { RejectionReasonModal } from "./RejectionReasonModal";
import { versionValidationSchema } from "./schemas/VersionValidationSchema";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useParams } from "react-router-dom";
import { PluginVersion } from "./models/PluginVersion";
import { ReviewType } from "./models/ReviewType";
import { processMarkdownToHTML } from "../../utils";

export const PendingVersionButtonGroup = () => {
  const { values, setTouched, handleSubmit } = useFormikContext<PluginVersion>();
  const [showModal, setShowModal] = useState(false);
  const { user } = useAuth0<ExtendedUser>();
  const { isLoading, versionActions } = useVersionActions();
  const { buttonStyle } = useTheme();
  const { versionId } = useParams() as { versionId: string };
  const { translate } = useLocalization();
  const isSystemAdmin = user?.user_metadata?.isSystemAdmin;

  const handleSave = async () => {
    try {
      values.htmlChangelog = await processMarkdownToHTML(values.markdownChangelog, values.changelogResources);
      await versionValidationSchema(translate).validate(values, { abortEarly: false });
      setTouched({});
      await versionActions.handleUpdatePending(parseInt(versionId), values);
    } catch (errors) {
      handleSubmit();
    }
  };

  if (isLoading) return <Loader />;

  if (isSystemAdmin) {
    return (
      <>
        <Button
          style={buttonStyle}
          type="button"
          onClick={() => versionActions.handleRequestReview(parseInt(versionId), ReviewType.Approved)}
        >
          {translate("plugins.approve")}
        </Button>
        <Button variant="danger" className="mx-2" type="button" onClick={() => setShowModal(true)}>
          {translate("plugins.reject")}
        </Button>
        {showModal && (
          <RejectionReasonModal
            onContinue={(rejectionReason) =>
              versionActions.handleRequestReview(parseInt(versionId), ReviewType.Rejected, rejectionReason)
            }
            onClose={() => setShowModal(false)}
          />
        )}
      </>
    );
  }

  return (
    <Button style={buttonStyle} className="me-2" type="button" onClick={handleSave}>
      {values.publishToRWSAppStore ? translate("plugins.submit") : translate("common.save")}
    </Button>
  );
};
